import { ChangeEvent, createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import GalleryModal from '../components/modais/gallery_modal'
import { makePostMissionAnswerImages } from '../../main/usecases/mission-answer/load-mission-answer-images-factory'
import { useGame } from '.'
import Webcam from 'react-webcam'
import { CameraContainer, TakePhoto, PickPhoto, ChangeCamera, CloseCamera, InfoBalloon, PickPhotoWrapper } from './style'
import { MdCameraswitch } from 'react-icons/md'
import { CgClose } from 'react-icons/cg'
import LoadingGame from '../components/loadings/loading_game'
import CameraIcon from '../assets/images/take-photo.png'
import FilePiker from '../assets/images/file-piker.png'
import { isMobile } from '../utils/general'

type ContextProps = {
  handleToggleGallery: () => void;
  loadImages: () => void;
  setOpenCamera: any;
  fileURL: any;
  setFileURL: any;
  file: any;
  setFile: any;
  isFilePicked: any;
  setIsFilePicked: any;
  images: any;
  isShowing?: boolean;
}

type GalleryProviderProps = {
  children: ReactNode;
}

export type IImageProps = {
  url: string;
  teamName: string;
  subtitle: string;
  mission: string;
}

const GalleryContext = createContext({} as ContextProps)

const GalleryProvider = ({ children }: GalleryProviderProps) => {
  const FACING_MODE_USER = 'user'
  const FACING_MODE_ENVIRONMENT = 'environment'

  const [isShowing, setIsShowing] = useState(false)
  const [images, setImages] = useState<IImageProps[]>([])

  const [fileURL, setFileURL] = useState<string | ArrayBuffer | null>('')
  const [file, setFile] = useState<any>(null)
  const [isFilePicked, setIsFilePicked] = useState(false)
  const [facingMode, setFacingMode] = useState(FACING_MODE_USER)
  const [camHelp, setCamHelp] = useState(true)
  const [fileHelp, setFileHelp] = useState(true)

  const webcamRef = useRef<any>(null)
  const fileInput = useRef<any>(null)

  const [openCamera, setOpenCamera] = useState<boolean>(false)
  const { game } = useGame()

  const urlToFile = (url:any, filename:any, mimeType:any) => {
    return (fetch(url)
      .then(function (res) { return res.arrayBuffer() })
      .then(function (buf) { return new File([buf], filename, { type: mimeType }) })
    )
  }

  const handleAddFile = (event: ChangeEvent<HTMLInputElement>) => {
    setOpenCamera(false)
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return

    const file = input.files[0]
    const objectUrl = URL.createObjectURL(file)

    if (file.type === 'video/mp4') {
      setIsFilePicked(false)
      setFileURL('')
      setFile(null)
      setIsFilePicked(false)
    } else {
      setFileURL(objectUrl)
      setFile(file)
      setIsFilePicked(true)
    }
  }

  const videoConstraints = {
    facingMode: FACING_MODE_ENVIRONMENT,
    aspectRatio: isMobile() ? screen.height / screen.width : screen.width / screen.height
  }

  const capture = useCallback(async () => {
    const imageSrc:any = webcamRef?.current?.getScreenshot()
    const fileToSend = await urlToFile(imageSrc, 'photo_to_upload.png', 'image/png')
    const objectUrl = URL.createObjectURL(fileToSend)

    setFileURL(objectUrl)
    setFile(fileToSend)
    setIsFilePicked(true)
    setOpenCamera(false)
  }, [webcamRef])

  function handleToggleGallery () {
    setIsShowing(!isShowing)
  }

  async function loadImages () {
    try {
      if (!!game && 'uuid' in game) {
        const imgArr:IImageProps[] = []
        const res = await makePostMissionAnswerImages(game.uuid).load()
        res['hydra:member'].forEach((item:any) => {
          if ('image' in item?.answer) {
            imgArr.push({
              url: item?.answer?.image,
              teamName: item?.team,
              subtitle: item?.answer.subtitle,
              mission: item?.mission
            })
          }
        })
        setImages(imgArr)
      }
    } catch (error) {
      console.error('GalleryProvider.loadImages', error)
    }
  }

  useEffect(() => {
    loadImages()
  }, [game])

  return (
    <GalleryContext.Provider value={{
      handleToggleGallery,
      loadImages,
      setOpenCamera,
      fileURL,
      setFileURL,
      file,
      setFile,
      isFilePicked,
      setIsFilePicked,
      images,
      isShowing
    }}>
      { children }
        <CameraContainer className={openCamera ? `${facingMode} active` : ''}>
          <LoadingGame/>
          <PickPhotoWrapper>
            <PickPhoto id="piker-photo" htmlFor="uploader">
              <img src={FilePiker}/>
            </PickPhoto>
            <input ref={fileInput} id="uploader" type="file" accept="image/*" onChange={handleAddFile} />

            {fileHelp &&
              <InfoBalloon className="left-side">
                <span>Busque uma foto</span>
                <button onClick={() => setFileHelp(false)}>
                  <CgClose />
                </button>
              </InfoBalloon>
            }
          </PickPhotoWrapper>

          <TakePhoto >
            <button onClick={capture}>
              <img src={CameraIcon}/>
            </button>

            {camHelp &&
              <InfoBalloon type="bottom">
                <span>Tire uma foto</span>
                <button onClick={() => setCamHelp(false)}>
                  <CgClose />
                </button>
              </InfoBalloon>
            }
          </TakePhoto>

          <ChangeCamera onClick={() => {
            setFacingMode(
              prev => prev === FACING_MODE_USER
                ? FACING_MODE_ENVIRONMENT
                : FACING_MODE_USER
            )
          }}>
            <MdCameraswitch size={34} />
          </ChangeCamera>

          <CloseCamera onClick={() => { setOpenCamera(false) }}>
            <CgClose />
          </CloseCamera>

          {openCamera &&
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                ...videoConstraints,
                facingMode
              }}
            />
          }
        </CameraContainer>
      {!!images.length && <GalleryModal isShowing={isShowing} images={images} />}
    </GalleryContext.Provider>
  )
}

const useGallery = () => {
  const context = useContext(GalleryContext)

  if (!context) throw new Error('useGallery must be used within a provider GalleryProvider')

  return { ...context }
}

export { GalleryContext, useGallery, GalleryProvider }
